import { of } from 'rxjs';
import {
	AuUtilsFunctions,
	BuildingKpiStatusFilterOptions,
	ComfortStatusFilterOptions,
	PredictiveMaintenanceFilterOptions,
	SystemRunOptions,
} from '@app/shared';
import { first, map, pluck, switchMap, take } from 'rxjs/operators';
import { getSingleBuildingId } from '@app/pages/dashboard/state/dashboard.selectors';
import { IAllDashboardBuildings } from '@app/pages/dashboard/interfaces/building.interfaces';
import { BuildingFloorQuery } from '@app/pages/dashboard/graphql/dashboard-qraphql.queries';
import { Apollo } from 'apollo-angular';
import { Store } from '@ngrx/store';

import gql from 'graphql-tag';
import { buildingsList } from '@layout/state/layout.selectors';
import { IFilterTenantsList } from '@shared/ui-components/au-configurable-filter/interfaces/filter-tenants-list.interface';
import {
	OrganizationsInBuilding,
	TenantsListQuery,
} from '@shared/ui-components/au-configurable-filter/graphql/filter.queries';
import { IOrganization } from '@shared/interfaces/organization.interface';

export class FetchFilterDataMethods {
	constructor(
		public apollo: Apollo,
		public store$: Store<any>
	) {}

	getComfortStatusFilterOptions() {
		return of(ComfortStatusFilterOptions);
	}

	getBuildingKpiStatuses() {
		return of(BuildingKpiStatusFilterOptions);
	}

	getSystemRunOptions() {
		return of(SystemRunOptions);
	}

	getPredictiveMaintenanceFilterOptions() {
		return of(PredictiveMaintenanceFilterOptions);
	}

	getOrganizations() {
		return this.apollo
			.query<IOrganization[]>({
				query: OrganizationsInBuilding,
			})
			.pipe(
				pluck('data', 'organizations'),
				map((organization: IOrganization[]) => {
					return organization.filter(item => item !== null);
				})
			);
	}

	getOrganizationsRelatedToBuilding() {
		return this.store$.select(getSingleBuildingId).pipe(
			take(1),
			switchMap(buildingId => {
				return this.apollo
					.query<IOrganization[]>({
						query: OrganizationsInBuilding,
						variables: {
							buildingId,
						},
					})
					.pipe(
						pluck('data', 'organizations'),
						map((organization: IOrganization[]) => {
							return organization.filter(item => item !== null);
						})
					);
			})
		);
	}

	getUserProperties() {
		return this.store$.select(buildingsList).pipe(first(hasValue => !!hasValue));
	}

	getTenants() {
		return this.apollo
			.query<IFilterTenantsList>({
				query: TenantsListQuery,
			})
			.pipe(
				pluck('data', 'tenants'),
				map(tenants => {
					return tenants.map(item => item.tenant);
				})
			);
	}

	getBuildingFloors() {
		return this.store$.select(getSingleBuildingId).pipe(
			take(1),
			switchMap((buildingId: string) => {
				return this.apollo
					.query<IAllDashboardBuildings>({
						query: BuildingFloorQuery,
						variables: {
							buildingsId: [buildingId],
						},
					})
					.pipe(
						pluck('data', 'buildings'),
						map(buildings => buildings[0].floor)
					);
			})
		);
	}

	getVendors() {
		return this.apollo
			.query<{ vendor: string }[]>({
				query: gql`
					query AssetFloorManagementQuery {
						locks {
							vendor
						}
					}
				`,
			})
			.pipe(
				pluck('data', 'locks'),
				map((locks: { vendor: string }[]) => {
					return locks
						.map(lock => lock.vendor)
						.filter(AuUtilsFunctions.onlyUnique)
						.map(vendor => {
							return {
								id: vendor,
								name: AuUtilsFunctions.capitalizeText(vendor),
							};
						});
				})
			);
	}

	getAccessStatuses() {
		return of([
			{
				id: 'active',
				name: 'Active',
			},
			{
				id: 'expired',
				name: 'Expired',
			},
		]);
	}
}
